import { toast } from 'sonner'
import { Eye, Loader2 } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { firebaseRtdb } from '@/lib/firebase'
import { useBoolean } from '@/hooks/use-boolean'
import { ref, onValue, off } from 'firebase/database'
import { useDeepEffect } from '@/hooks/use-deep-effect'
import { useState, useEffect, useRef, useCallback } from 'react'
import AgoraRTC, {
  type IAgoraRTCClient,
  type IAgoraRTCRemoteUser,
} from 'agora-rtc-sdk-ng'

import { Program, ProgramStatus } from './program.types'

const appId = import.meta.env.VITE_AGORA_APP_ID

AgoraRTC.setLogLevel(4)

type Props = {
  programId: string
  channel: string
  token: string
  uid: string
}

export default function LiveStreamPlayer({
  programId,
  channel,
  token,
  uid,
}: Props) {
  const [isConnected, setIsConnected] = useState(false)
  const [isConnecting, setIsConnecting] = useState(false)
  const [hasStream, setHasStream] = useState(false)
  const [viewerCount, setViewerCount] = useState(0)
  const isStreaming = useBoolean(false)

  const clientRef = useRef<IAgoraRTCClient | null>(null)
  const remoteUsersRef = useRef<Record<string, IAgoraRTCRemoteUser>>({})
  const remotePlayerRef = useRef<HTMLDivElement>(null)
  const statsIntervalRef = useRef<NodeJS.Timeout | null>(null)
  const videoElementRef = useRef<HTMLVideoElement | null>(null)
  const updateDimensionsRef = useRef<(() => void) | null>(null)

  // Listen to audience count changes
  useEffect(() => {
    if (!programId) return

    const audienceRef = ref(firebaseRtdb, `live/${programId}/audience`)

    const unsubscribe = onValue(
      audienceRef,
      (snapshot) => {
        const data = snapshot.val()
        const count = data ? Object.keys(data).length : 0
        setViewerCount(count)
      },
      (error) => {
        console.error('Error listening to audience:', error)
      },
    )

    return () => {
      off(audienceRef, 'value', unsubscribe)
    }
  }, [programId])

  const handleUserPublished = useCallback(
    async (user: IAgoraRTCRemoteUser, mediaType: 'video' | 'audio') => {
      const client = clientRef.current
      if (!client) return

      try {
        await client.subscribe(user, mediaType)

        if (
          mediaType === 'video' &&
          user.videoTrack &&
          remotePlayerRef.current
        ) {
          user.videoTrack.play(remotePlayerRef.current)
          setHasStream(true)

          setTimeout(() => {
            const videoElement = remotePlayerRef.current?.querySelector('video')
            if (videoElement) {
              videoElementRef.current = videoElement

              videoElement.style.objectFit = 'contain'
            }
          }, 100)
        }

        if (mediaType === 'audio' && user.audioTrack) {
          user.audioTrack.play()
        }

        remoteUsersRef.current[user.uid.toString()] = user
        setViewerCount(Object.keys(remoteUsersRef.current).length)
      } catch (err) {
        console.error('Failed to subscribe:', err)
      }
    },
    [],
  )

  const handleUserUnpublished = useCallback(
    (_: IAgoraRTCRemoteUser, mediaType: 'video' | 'audio') => {
      if (mediaType === 'video') {
        setHasStream(false)

        // Clean up video element reference
        if (videoElementRef.current && updateDimensionsRef.current) {
          videoElementRef.current.removeEventListener(
            'loadedmetadata',
            updateDimensionsRef.current,
          )
          videoElementRef.current = null
          updateDimensionsRef.current = null
        }
      }
    },
    [],
  )

  const initClient = useCallback(async () => {
    const client = AgoraRTC.createClient({
      mode: 'live',
      codec: 'h264',
    })

    await client.setClientRole('audience')

    client.on('user-published', handleUserPublished)
    client.on('user-unpublished', handleUserUnpublished)

    clientRef.current = client
    return client
  }, [handleUserPublished, handleUserUnpublished])

  const startStatsMonitoring = useCallback(() => {
    if (statsIntervalRef.current) {
      clearInterval(statsIntervalRef.current)
    }

    statsIntervalRef.current = setInterval(() => {
      const client = clientRef.current
      if (!client) return
    }, 1000)
  }, [])

  const joinStream = useCallback(async () => {
    if (!appId) {
      toast.error('ไม่พบ Agora App ID')
      return
    }

    if (!channel) {
      toast.error('ไม่พบ Channel Name')
      return
    }

    setIsConnecting(true)

    try {
      if (token && uid) {
        const client = await initClient()
        await client.join(appId, channel, token, uid)

        setIsConnected(true)
        startStatsMonitoring()
      }
    } catch (err: unknown) {
      console.error('Failed to join:', err)
      toast.error('ไลฟ์หมดเวลา หรืออาจจบลงแล้ว')
    } finally {
      setIsConnecting(false)
    }
  }, [channel, token, initClient, startStatsMonitoring, uid])

  const leaveStream = useCallback(async () => {
    try {
      if (statsIntervalRef.current) {
        clearInterval(statsIntervalRef.current)
        statsIntervalRef.current = null
      }

      if (clientRef.current) {
        await clientRef.current.leave()
        clientRef.current = null
      }

      setIsConnected(false)
      setHasStream(false)

      // Clean up video element reference
      if (videoElementRef.current && updateDimensionsRef.current) {
        videoElementRef.current.removeEventListener(
          'loadedmetadata',
          updateDimensionsRef.current,
        )
        videoElementRef.current = null
        updateDimensionsRef.current = null
      }

      remoteUsersRef.current = {}
      setViewerCount(0)
    } catch (err: unknown) {
      console.error('Failed to leave:', err)
      toast.error('ไลฟ์หมดเวลา หรืออาจจบลงแล้ว')
    }
  }, [])

  useEffect(() => {
    return () => {
      if (statsIntervalRef.current) {
        clearInterval(statsIntervalRef.current)
      }
      if (clientRef.current) {
        clientRef.current.leave()
      }
    }
  }, [])

  useDeepEffect(() => {
    joinStream()
    return () => {
      leaveStream()
    }
  }, [joinStream])

  useDeepEffect(() => {
    if (!programId) return
    const programRef = ref(firebaseRtdb, `programs/${programId}`)
    const unsubscribe = onValue(
      programRef,
      (snapshot) => {
        const newProgramData = snapshot.val() as Program

        if (newProgramData && newProgramData.status === ProgramStatus.LIVE) {
          isStreaming.onTrue()
        } else {
          isStreaming.onFalse()
        }
      },
      (error) => {
        console.error('Error monitoring program:', error)
      },
    )

    return () => {
      off(programRef, 'value', unsubscribe)
    }
  }, [programId])

  if (isConnecting)
    return (
      <div className="w-full aspect-video flex items-center justify-center">
        <Loader2 className="w-10 h-10 animate-spin" />
      </div>
    )

  return (
    <div className={`relative bg-black overflow-hidden h-full w-full`}>
      <div className="absolute top-4 right-4 flex gap-2 z-10">
        {isStreaming.value ? (
          <Badge variant="destructive" className="bg-red-500">
            ● LIVE
          </Badge>
        ) : (
          <Badge variant="secondary">● OFFLINE</Badge>
        )}
        {isStreaming.value && (
          <Badge variant="secondary">
            {viewerCount} <Eye className="w-4 h-4" />
          </Badge>
        )}
      </div>

      <div className="w-full h-full flex items-center justify-center relative">
        <div
          ref={remotePlayerRef}
          className="overflow-hidden relative w-full h-full"
        >
          {!hasStream && (
            <div className="text-center text-muted-foreground absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
              <div className="text-4xl mb-4">📺</div>
              <h3 className="text-lg font-medium mb-2">
                {isConnected ? 'ไม่มีสตรีม' : 'ไม่ได้เชื่อมต่อ'}
              </h3>
              <p className="text-sm">
                {isConnected
                  ? 'รอให้ผู้ถ่ายทอดเริ่มสตรีม...'
                  : 'กดเข้าร่วมสตรีมเพื่อรับชม'}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
